import React, { useState, useEffect } from 'react';
import { Field, getIn } from 'formik';
import { Col } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { FormikSelect, NestedAttributes, CreatableSelect } from '../../components';
import { levelsEmployeeDnc } from './FormOptions';
import { createTopicRequest, debounceIndexTopicsRequest } from '../../requests/topics';
import { createNecessityAreaRequest, indexNecessityAreaRequest } from '../../requests/necessityAreas';

const EmployeeDncRow = ({
  setFieldValue,
  setFieldTouched,
  attributes,
  errors,
  touched,
  index,
  topics,
  setTopics,
  necessityAreas,
  setNecessityAreas,
  onRequest,
  setOnRequest,
  showLabels,
  invisibleLabels
}) => {
  const dispatch = useDispatch();
  const rowPath = `dnc[employeeDncsAttributes][${index}]`;
  const { topic, necessityArea } = attributes;

  const handleNewNecessityArea = newValue => {
    setOnRequest(true);
    createNecessityAreaRequest({
      dispatch,
      params: { necessity_area: { name: newValue } },
      formData: true,
      successCallback: response => {
        const addNew = { value: response.data.id, label: newValue };
        setFieldValue(`${rowPath}[necessityAreaId]`, response.data.id);
        setFieldValue(`${rowPath}[necessityArea]`, addNew);
        setNecessityAreas([...necessityAreas, addNew]);
      },
      callback: () => setOnRequest(false)
    });
  };

  const handleNewTopic = newValue => {
    setOnRequest(true);
    createTopicRequest({
      dispatch,
      params: { topic: { name: newValue } },
      formData: true,
      successCallback: response => {
        const addNew = { value: response.data.id, label: newValue };
        setFieldValue(`${rowPath}[topicId]`, response.data.id);
        setFieldValue(`${rowPath}[topic]`, addNew);
        setTopics([...topics, addNew]);
      },
      callback: () => setOnRequest(false)
    });
  };

  const labelClass = invisibleLabels ? 'invisible' : 'font-weight-bold';

  return (
    <>
      <Col md={4}>
        <Field name={`${rowPath}[topicId]`}>
          {({ field }) => (
            <CreatableSelect
              {...field}
              abbr
              label={showLabels || invisibleLabels ? 'TEMA' : ''}
              labelClass={labelClass}
              isClearable
              placeholder="Seleccionar o crear tema"
              onCreateOption={handleNewTopic}
              options={topics}
              onChange={data => {
                setFieldValue(`${rowPath}[topic]`, data);
                setFieldValue(field.name, data ? data.value : '');
              }}
              setFieldTouched={() => setFieldTouched(field.name)}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
              isDisabled={onRequest}
              isLoading={onRequest}
              value={topic}
            />
          )}
        </Field>
      </Col>
      <Col md={3}>
        <Field name={`${rowPath}[necessityAreaId]`}>
          {({ field }) => (
            <CreatableSelect
              {...field}
              label={showLabels || invisibleLabels ? 'ÁREA DE NECESIDAD' : ''}
              labelClass={labelClass}
              abbr
              isClearable
              placeholder="Seleccionar o crear área"
              onCreateOption={handleNewNecessityArea}
              options={necessityAreas}
              onChange={data => {
                setFieldValue(`${rowPath}[necessityArea]`, data);
                setFieldValue(`${rowPath}[necessityAreaId]`, data ? data.value : '');
              }}
              setFieldTouched={() => setFieldTouched(field.name)}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
              isDisabled={onRequest}
              isLoading={onRequest}
              value={necessityArea}
            />
          )}
        </Field>
      </Col>
      <Col md={2} className="px-1">
        <Field name={`${rowPath}[depthLevel]`}>
          {({ field }) => (
            <FormikSelect
              {...field}
              label={showLabels || invisibleLabels ? 'NIVEL DE PROF.' : ' '}
              labelClass={labelClass}
              placeholder="Seleccionar"
              options={levelsEmployeeDnc}
              defaultValue={attributes.depthLevel}
              onChange={data => setFieldValue(field.name, data ? data.value : '')}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
      <Col md={2} className="px-1">
        <Field name={`${rowPath}[urgencyLevel]`}>
          {({ field }) => (
            <FormikSelect
              {...field}
              label={showLabels || invisibleLabels ? 'NIVEL DE URGENCIA' : ''}
              labelClass={labelClass}
              placeholder="Seleccionar"
              options={levelsEmployeeDnc}
              defaultValue={attributes.urgencyLevel}
              onChange={data => setFieldValue(field.name, data ? data.value : '')}
              error={getIn(errors, field.name)}
              touched={getIn(touched, field.name)}
            />
          )}
        </Field>
      </Col>
    </>
  );
};

const EmployeeDncInputs = ({ errors, touched, values, setFieldValue, setFieldTouched, employeeId }) => {
  const { employeeDncsAttributes } = values.dnc;
  const dispatch = useDispatch();
  const [topics, setTopics] = useState([]);
  const [necessityAreas, setNecessityAreas] = useState([]);
  const [onRequest, setOnRequest] = useState(false);

  const resultFetchData = response => {
    return response.map(element => ({
      label: element.name,
      value: element.id,
      necessityAreas: element.necessity_areas
    }));
  };

  useEffect(() => {
    debounceIndexTopicsRequest({
      dispatch,
      params: {
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => setTopics(resultFetchData(response.data.data))
    });
    indexNecessityAreaRequest({
      dispatch,
      params: {
        sort_column: 'name',
        paginate: false
      },
      successCallback: response => setNecessityAreas(resultFetchData(response.data.data))
    });
  }, [dispatch]);

  const firstVisibleIndex = employeeDncsAttributes.findIndex(body => !body._destroy);

  const mapResults = employeeDncsAttributes.map((body, index) => {
    if (body._destroy) {
      return undefined;
    }

    return (
      <EmployeeDncRow
        key={body.id || `new-row-${index}`}
        attributes={body}
        errors={errors}
        touched={touched}
        index={index}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        topics={topics}
        setTopics={setTopics}
        necessityAreas={necessityAreas}
        setNecessityAreas={setNecessityAreas}
        onRequest={onRequest}
        setOnRequest={setOnRequest}
        showLabels={index === firstVisibleIndex}
        invisibleLabels={index > firstVisibleIndex}
      />
    );
  });

  mapResults.push(
    <React.Fragment key="placeholder-row">
      <Col md={4}>
        <div className="p-2 text-muted">Añadir tema...</div>
      </Col>
      <Col md={3} />
      <Col md={2} />
      <Col md={2} />
    </React.Fragment>
  );

  return (
    <NestedAttributes
      removeFirstItem
      mapInputs={mapResults}
      arrayValues={employeeDncsAttributes}
      setFieldValue={setFieldValue}
      valuePath="dnc[employeeDncsAttributes]"
      newAttributes={{
        active: true,
        cost: 0,
        depthLevel: '',
        urgencyLevel: '',
        status: 'pending',
        topicId: '',
        topic: null,
        necessityAreaId: '',
        necessityArea: null,
        employeeId
      }}
    />
  );
};

export default EmployeeDncInputs;
